import { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent } from "../components/ui/dialog";
import { Button } from "../components/ui/button";
import { Switch } from "../components/ui/switch";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "../components/ui/alert-dialog";
import { toast } from "../components/ui/sonner";
import { Shuffle, Settings2, Save, Loader2, BarChart3, ArrowRight, Check, Minus, Plus, History, Scale, Hash, DollarSign, SlidersHorizontal, UserX } from "lucide-react";
import { useAppearance } from "../contexts/AppearanceContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const NAV_ITEMS = [
  { key: "auto", label: "Novos Negócios", icon: Settings2 },
  { key: "manual", label: "Rotacionar", icon: Shuffle },
  { key: "dashboard", label: "Equilíbrio", icon: BarChart3 },
];

export default function RobinRoundPage() {
  const navigate = useNavigate();
  const appearance = useAppearance();
  const tc = appearance.colors?.["500"] || "#3B82F6";
  const tc50 = appearance.colors?.["50"] || "#EFF6FF";

  const [open, setOpen] = useState(true);
  const [tab, setTab] = useState("auto");

  const [pipelines, setPipelines] = useState([]);
  const [stages, setStages] = useState([]);
  const [users, setUsers] = useState([]);
  const [numericFields, setNumericFields] = useState([]);

  // Auto config state
  const [selectedPipeline, setSelectedPipeline] = useState("");
  const [config, setConfig] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  // Balance state
  const [balance, setBalance] = useState([]);
  const [balancePipeline, setBalancePipeline] = useState("");
  const [logs, setLogs] = useState([]);

  // Manual rotation state
  const [rotPipeline, setRotPipeline] = useState("");
  const [rotStageFilter, setRotStageFilter] = useState("all");
  const [rotParticipants, setRotParticipants] = useState([]);
  const [rotCriteria, setRotCriteria] = useState("quantity");
  const [rotSourceUsers, setRotSourceUsers] = useState([]);
  const [allUsersWithDeleted, setAllUsersWithDeleted] = useState([]);
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [rotating, setRotating] = useState(false);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/pipelines`).then(r => { setPipelines(r.data); return r.data; }),
      axios.get(`${API}/users`).then(r => { const u = r.data.filter(u => u.status !== "pending"); setUsers(u); return u; }),
      axios.get(`${API}/custom-fields?module=negocios`).then(r => {
        setNumericFields(r.data.filter(f => f.field_type === "number" || f.field_type === "currency"));
      }),
      axios.get(`${API}/robin-round/configs`).then(r => r.data).catch(() => []),
      axios.get(`${API}/robin-round/log`).then(r => setLogs(r.data)).catch(() => {}),
    ]).then(([pips, usrs, , configs]) => {
      // Auto-select first pipeline with saved config, or first pipeline
      if (pips.length > 0) {
        const saved = configs.find(c => c.enabled);
        const firstId = saved ? saved.pipeline_id : pips[0].id;
        setSelectedPipeline(firstId);
        setBalancePipeline(firstId);
        setRotPipeline(firstId);
      }
    }).finally(() => setLoading(false));
  }, []);

  // Load config when pipeline changes (auto tab)
  useEffect(() => {
    if (!selectedPipeline) return;
    axios.get(`${API}/pipeline-stages?pipeline_id=${selectedPipeline}`).then(r => setStages(r.data));
    axios.get(`${API}/robin-round/config/${selectedPipeline}`).then(r => {
      const cfg = r.data;
      // Merge saved participants with current users (add new users, keep saved weights)
      const savedMap = {};
      (cfg.participants || []).forEach(p => { savedMap[p.user_id] = p; });
      const merged = users.map(u => savedMap[u.user_id] || { user_id: u.user_id, name: u.name, weight: 1, active: true });
      cfg.participants = merged;
      setConfig(cfg);
    });
  }, [selectedPipeline, users]);

  // Load stages and source users when rotation pipeline changes
  useEffect(() => {
    if (!rotPipeline) return;
    axios.get(`${API}/pipeline-stages?pipeline_id=${rotPipeline}`).then(r => setStages(r.data));
    setRotParticipants(users.map(u => ({ user_id: u.user_id, name: u.name, weight: 1, active: true })));
    setPreview(null);
    setRotSourceUsers([]);
    axios.get(`${API}/robin-round/users-with-deals?pipeline_id=${rotPipeline}`).then(r => setAllUsersWithDeleted(r.data || [])).catch(() => setAllUsersWithDeleted([]));
  }, [rotPipeline]); // eslint-disable-line

  // Load balance when tab changes to dashboard or pipeline changes
  useEffect(() => {
    if (tab !== "dashboard") return;
    if (!balancePipeline) return;
    axios.get(`${API}/robin-round/balance/${balancePipeline}`).then(r => setBalance(r.data)).catch(() => setBalance([]));
    axios.get(`${API}/robin-round/log`).then(r => setLogs(r.data)).catch(() => {});
  }, [tab, balancePipeline]);

  const handleClose = (v) => { if (!v) navigate(-1); setOpen(v); };

  const saveConfig = async () => {
    if (!selectedPipeline || !config) return;
    setSaving(true);
    try {
      await axios.put(`${API}/robin-round/config/${selectedPipeline}`, config);
      toast.success("Configuração salva!");
    } catch { toast.error("Erro ao salvar."); }
    setSaving(false);
  };

  const toggleParticipant = (list, setList, userId, field, val) => {
    const updater = (prev) => {
      const items = Array.isArray(prev) ? prev : prev?.participants;
      const updated = (items || []).map(p => p.user_id === userId ? { ...p, [field]: val } : p);
      if (Array.isArray(prev)) return updated;
      return { ...prev, participants: updated };
    };
    setList(updater);
  };

  const handlePreview = async () => {
    if (!rotPipeline) { toast.error("Selecione um pipeline."); return; }
    setPreviewing(true); setPreview(null);
    try {
      const res = await axios.post(`${API}/robin-round/preview`, {
        pipeline_id: rotPipeline,
        stage_ids: rotStageFilter === "all" ? [] : [rotStageFilter],
        participants: rotParticipants, criteria: rotCriteria,
        source_user_ids: rotSourceUsers,
      });
      setPreview(res.data);
    } catch (err) { toast.error(err.response?.data?.detail || "Erro."); }
    setPreviewing(false);
  };

  const handleRotate = async () => {
    setRotating(true);
    try {
      const res = await axios.post(`${API}/robin-round/rotate`, {
        pipeline_id: rotPipeline,
        stage_ids: rotStageFilter === "all" ? [] : [rotStageFilter],
        participants: rotParticipants, criteria: rotCriteria,
        source_user_ids: rotSourceUsers,
      });
      toast.success(`${res.data.changed} negócios redistribuídos!`);
      setPreview(null);
      axios.get(`${API}/robin-round/log`).then(r => setLogs(r.data)).catch(() => {});
    } catch (err) { toast.error(err.response?.data?.detail || "Erro."); }
    setRotating(false);
  };

  // ── Criteria cards ──
  const CriteriaCards = ({ value, onChange }) => {
    const options = [
      { key: "quantity", label: "Quantidade", desc: "1 para cada", icon: Hash },
      { key: "mrr", label: "Valor MRR", desc: "Equalizar MRR", icon: DollarSign },
      ...numericFields.map(f => ({ key: f.field_name, label: f.field_label, desc: "Campo numérico", icon: SlidersHorizontal })),
    ];
    return (
      <div>
        <Label className="text-slate-700 mb-2 block">Critério de distribuição</Label>
        <div className="grid grid-cols-4 gap-2">
          {options.map(o => {
            const Icon = o.icon;
            const sel = value === o.key;
            return (
              <button key={o.key} type="button" onClick={() => onChange(o.key)}
                className={`flex items-center gap-2.5 p-3 rounded-lg border-2 transition-colors text-left ${sel ? "bg-transparent" : "border-slate-200 text-slate-500 hover:border-slate-300"}`}
                style={sel ? { borderColor: tc, color: tc } : undefined}>
                <Icon className="w-4 h-4 flex-shrink-0" strokeWidth={1.5} />
                <div>
                  <span className="text-xs font-semibold block">{o.label}</span>
                  <span className="text-[10px] opacity-70">{o.desc}</span>
                </div>
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  // ── Participants list ──
  const ParticipantsList = ({ participants, onToggle }) => (
    <div>
      <Label className="text-slate-700 mb-2 block">Participantes</Label>
      <div className="border border-slate-200 rounded-lg divide-y divide-slate-100 max-h-48 overflow-y-auto">
        {(participants || []).map(p => (
          <div key={p.user_id} className="flex items-center gap-3 px-3 py-2">
            <Switch checked={p.active !== false} onCheckedChange={v => onToggle(p.user_id, "active", v)} />
            <span className={`flex-1 text-sm ${p.active !== false ? "text-slate-800 font-medium" : "text-slate-400 line-through"}`}>
              {p.name || p.user_id}
            </span>
            <div className="flex items-center gap-1">
              <span className="text-[10px] text-slate-400">Peso:</span>
              <button type="button" onClick={() => onToggle(p.user_id, "weight", Math.max(1, (p.weight || 1) - 1))}
                className="w-5 h-5 flex items-center justify-center rounded border border-slate-200 hover:bg-slate-50 text-slate-500"><Minus className="w-2.5 h-2.5" /></button>
              <span className="w-5 text-center text-xs font-semibold text-slate-700">{p.weight || 1}</span>
              <button type="button" onClick={() => onToggle(p.user_id, "weight", (p.weight || 1) + 1)}
                className="w-5 h-5 flex items-center justify-center rounded border border-slate-200 hover:bg-slate-50 text-slate-500"><Plus className="w-2.5 h-2.5" /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  if (loading) return <div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="w-[calc(100%-1rem)] max-w-4xl !p-0 overflow-hidden" data-testid="robin-round-modal">
        <div className="flex h-[80vh]">
          {/* Sidebar */}
          <div className="w-44 flex-shrink-0 border-r border-slate-200 bg-slate-50 py-4 px-2 space-y-0.5 overflow-y-auto">
            <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider px-2 mb-2">Robin Round</p>
            {NAV_ITEMS.map(item => {
              const Icon = item.icon;
              const sel = tab === item.key;
              return (
                <button key={item.key} type="button" onClick={() => setTab(item.key)}
                  className={`w-full flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-sm transition-all ${sel ? "text-white font-medium" : "text-slate-600 hover:bg-slate-100"}`}
                  style={sel ? { backgroundColor: tc } : undefined} data-testid={`nav-${item.key}`}>
                  <Icon className={`w-4 h-4 flex-shrink-0 ${sel ? "text-white" : "text-slate-400"}`} strokeWidth={1.5} />
                  <span className="text-[12px] truncate">{item.label}</span>
                </button>
              );
            })}
          </div>

          {/* Content */}
          <div className="flex-1 p-6 flex flex-col overflow-hidden">
            {/* ─── Novos Negócios ─── */}
            {tab === "auto" && (
              <div className="flex-1 flex flex-col overflow-hidden">
                <div className="mb-4 flex-shrink-0">
                  <h2 className="font-heading text-lg font-bold text-slate-900 flex items-center gap-2">
                    <Settings2 className="w-5 h-5" style={{ color: tc }} />Novos Negócios
                  </h2>
                  <p className="text-xs text-slate-500 mt-1">Quando um negócio é criado sem responsável, o sistema distribui automaticamente.</p>
                </div>
                <div className="flex-1 space-y-4 overflow-y-auto pr-1">
                  <div>
                    <Label className="text-slate-700 mb-2 block">Pipeline</Label>
                    <Select value={selectedPipeline} onValueChange={setSelectedPipeline}>
                      <SelectTrigger className="h-9" data-testid="auto-pipeline-select"><SelectValue placeholder="Selecionar pipeline..." /></SelectTrigger>
                      <SelectContent>{pipelines.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>

                  {config && selectedPipeline && (
                    <>
                      <div className="flex items-center gap-3 p-3 rounded-lg" style={{ backgroundColor: tc50 }}>
                        <Switch data-testid="auto-enabled" checked={config.enabled} onCheckedChange={v => setConfig({ ...config, enabled: v })} />
                        <div>
                          <span className="text-sm font-medium text-slate-800">Ativar Robin Round</span>
                          <p className="text-[11px] text-slate-500">Distribui automaticamente novos negócios sem responsável.</p>
                        </div>
                      </div>

                      <CriteriaCards value={config.criteria} onChange={v => setConfig({ ...config, criteria: v })} />

                      <ParticipantsList
                        participants={config.participants}
                        onToggle={(uid, f, v) => toggleParticipant(config, setConfig, uid, f, v)}
                      />

                      <Button data-testid="save-auto-config" onClick={saveConfig} disabled={saving} className="w-full text-white hover:opacity-90" style={{ backgroundColor: tc }}>
                        {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                        Salvar configuração
                      </Button>
                    </>
                  )}
                </div>
              </div>
            )}

            {/* ─── Rotacionar Existentes ─── */}
            {tab === "manual" && (
              <div className="flex-1 flex flex-col overflow-hidden">
                <div className="mb-4 flex-shrink-0">
                  <h2 className="font-heading text-lg font-bold text-slate-900 flex items-center gap-2">
                    <Shuffle className="w-5 h-5" style={{ color: tc }} />Rotacionar Existentes
                  </h2>
                  <p className="text-xs text-slate-500 mt-1">Redistribui negócios que já estão no sistema entre a equipe.</p>
                </div>
                <div className="flex-1 space-y-4 overflow-y-auto pr-1">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-slate-700 mb-2 block">Pipeline</Label>
                      <Select value={rotPipeline} onValueChange={v => { setRotPipeline(v); setPreview(null); }}>
                        <SelectTrigger className="h-9" data-testid="rot-pipeline-select"><SelectValue placeholder="Selecionar..." /></SelectTrigger>
                        <SelectContent>{pipelines.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-slate-700 mb-2 block">Etapas</Label>
                      <Select value={rotStageFilter} onValueChange={v => { setRotStageFilter(v); setPreview(null); }}>
                        <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">Todas as etapas</SelectItem>
                          {stages.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {/* Source users filter */}
                  <div>
                    <Label className="text-slate-700 mb-2 block flex items-center gap-1.5">
                      <UserX className="w-3.5 h-3.5 text-slate-400" />De quais responsáveis redistribuir?
                    </Label>
                    {allUsersWithDeleted.length > 0 ? (
                      <div className="border border-slate-200 rounded-lg divide-y divide-slate-100">
                        {allUsersWithDeleted.map(u => {
                          const active = rotSourceUsers.includes(u.user_id);
                          return (
                            <div key={u.user_id} className="flex items-center gap-3 px-3 py-2">
                              <Switch checked={active} onCheckedChange={v => {
                                setRotSourceUsers(prev => v ? [...prev, u.user_id] : prev.filter(id => id !== u.user_id));
                                setPreview(null);
                              }} />
                              <span className={`flex-1 text-sm ${active ? (u.deleted ? "text-rose-600 font-medium italic" : "text-slate-800 font-medium") : (u.deleted ? "text-rose-400 italic line-through" : "text-slate-400 line-through")}`}>
                                {u.name}
                              </span>
                              <span className="text-[11px] text-slate-400 tabular-nums">{u.deal_count} negócio{u.deal_count !== 1 ? "s" : ""}</span>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="text-center py-4 text-xs text-slate-400 border border-dashed border-slate-200 rounded-lg">
                        Nenhum usuário com negócios ativos neste pipeline.
                      </div>
                    )}
                  </div>

                  <CriteriaCards value={rotCriteria} onChange={v => { setRotCriteria(v); setPreview(null); }} />

                  <ParticipantsList
                    participants={rotParticipants}
                    onToggle={(uid, f, v) => {
                      setRotParticipants(prev => prev.map(p => p.user_id === uid ? { ...p, [f]: v } : p));
                      setPreview(null);
                    }}
                  />

                  <Button data-testid="preview-btn" variant="outline" onClick={handlePreview} disabled={previewing || !rotPipeline} className="w-full">
                    {previewing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Scale className="w-4 h-4 mr-2" />}
                    Gerar Preview
                  </Button>

                  {/* Preview table */}
                  {preview && (
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <p className="text-sm text-slate-600"><strong>{preview.total_changes}</strong> alterações de <strong>{preview.total_deals}</strong> negócios</p>
                        {preview.total_changes > 0 && (
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button data-testid="rotate-btn" size="sm" className="text-white" style={{ backgroundColor: tc }}>
                                <Shuffle className="w-3.5 h-3.5 mr-1.5" />Rotacionar
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Confirmar rotação?</AlertDialogTitle>
                                <AlertDialogDescription>{preview.total_changes} negócios terão o responsável alterado. Será registrado no histórico.</AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Cancelar</AlertDialogCancel>
                                <AlertDialogAction onClick={handleRotate} disabled={rotating} style={{ backgroundColor: tc }}>
                                  {rotating && <Loader2 className="w-4 h-4 animate-spin mr-1" />}Confirmar
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        )}
                      </div>

                      {preview.changes.length > 0 ? (
                        <div className="border border-slate-200 rounded-lg overflow-hidden max-h-48 overflow-y-auto">
                          <table className="w-full text-xs">
                            <thead className="bg-slate-50 text-slate-500 sticky top-0">
                              <tr>
                                <th className="px-3 py-1.5 text-left font-medium">Negócio</th>
                                <th className="px-3 py-1.5 text-left font-medium">De</th>
                                <th className="px-3 py-1.5 text-center font-medium w-6"></th>
                                <th className="px-3 py-1.5 text-left font-medium">Para</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                              {preview.changes.map(c => (
                                <tr key={c.deal_id}>
                                  <td className="px-3 py-1.5 text-slate-800 font-medium truncate max-w-[150px]">{c.titulo}</td>
                                  <td className="px-3 py-1.5 text-slate-500">{c.from_name}</td>
                                  <td className="px-3 py-1.5 text-center"><ArrowRight className="w-3 h-3 text-slate-400 mx-auto" /></td>
                                  <td className="px-3 py-1.5 font-medium" style={{ color: tc }}>{c.to_name}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      ) : (
                        <div className="text-center py-4 text-xs text-slate-400">Nenhuma alteração necessária.</div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* ─── Equilíbrio ─── */}
            {tab === "dashboard" && (
              <div className="flex-1 flex flex-col overflow-hidden">
                <div className="mb-4 flex-shrink-0">
                  <h2 className="font-heading text-lg font-bold text-slate-900 flex items-center gap-2">
                    <BarChart3 className="w-5 h-5" style={{ color: tc }} />Equilíbrio da Equipe
                  </h2>
                  <p className="text-xs text-slate-500 mt-1">Visualize como os negócios estão distribuídos e o histórico de rotações.</p>
                </div>
                <div className="flex-1 space-y-4 overflow-y-auto pr-1">
                  <div>
                    <Label className="text-slate-700 mb-2 block">Pipeline</Label>
                    <Select value={balancePipeline} onValueChange={setBalancePipeline}>
                      <SelectTrigger className="h-9"><SelectValue placeholder="Selecionar pipeline..." /></SelectTrigger>
                      <SelectContent>{pipelines.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>

                  {balance.length > 0 && (
                    <div className="border border-slate-200 rounded-lg overflow-hidden">
                      <table className="w-full text-sm">
                        <thead className="bg-slate-50 text-slate-500 text-xs">
                          <tr>
                            <th className="px-3 py-2 text-left font-medium">Responsável</th>
                            <th className="px-3 py-2 text-right font-medium">Negócios</th>
                            <th className="px-3 py-2 text-right font-medium">MRR</th>
                            <th className="px-3 py-2 text-right font-medium">Total</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                          {balance.sort((a, b) => b.count - a.count).map(b => {
                            const maxCount = Math.max(...balance.map(x => x.count), 1);
                            return (
                              <tr key={b.user_id}>
                                <td className="px-3 py-2">
                                  <div className="flex items-center gap-2">
                                    <div className="w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold text-white" style={{ backgroundColor: tc }}>
                                      {(b.name || "?").charAt(0).toUpperCase()}
                                    </div>
                                    <span className="font-medium text-slate-800 text-sm">{b.name}</span>
                                  </div>
                                </td>
                                <td className="px-3 py-2 text-right">
                                  <div className="flex items-center justify-end gap-2">
                                    <div className="w-16 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                                      <div className="h-full rounded-full" style={{ width: `${(b.count / maxCount) * 100}%`, backgroundColor: tc }} />
                                    </div>
                                    <span className="font-semibold text-slate-700 text-xs w-5 text-right">{b.count}</span>
                                  </div>
                                </td>
                                <td className="px-3 py-2 text-right text-xs text-slate-600">R$ {b.mrr?.toLocaleString("pt-BR", { minimumFractionDigits: 2 })}</td>
                                <td className="px-3 py-2 text-right text-xs font-semibold text-slate-800">R$ {b.total?.toLocaleString("pt-BR", { minimumFractionDigits: 2 })}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                  {balancePipeline && balance.length === 0 && (
                    <div className="text-center py-6 text-xs text-slate-400">Nenhum negócio ativo neste pipeline.</div>
                  )}

                  {/* Log — fills remaining vertical space */}
                  <div className="flex-1 flex flex-col min-h-0">
                    <Label className="text-slate-700 mb-2 block flex items-center gap-1.5 flex-shrink-0"><History className="w-3.5 h-3.5" />Histórico de Rotações</Label>
                    {logs.length > 0 ? (
                      <div className="space-y-1.5 flex-1 overflow-y-auto min-h-0">
                        {logs.map((log, i) => (
                          <div key={i} className="flex items-start gap-2 px-3 py-2 rounded-lg bg-slate-50 text-xs">
                            <Shuffle className="w-3.5 h-3.5 text-slate-400 mt-0.5 flex-shrink-0" />
                            <div className="flex-1 min-w-0">
                              <p className="text-slate-700">{log.description}</p>
                              <p className="text-[10px] text-slate-400 mt-0.5">{log.user_name} - {new Date(log.created_at).toLocaleString("pt-BR")}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-4 text-xs text-slate-400">Nenhuma rotação realizada.</div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
